import { useEffect, useRef, useState } from 'react';

const stats = [
  { prefix: '$', value: 48, suffix: 'B', label: 'Addressable Market', sub: 'U.S. payments-tech spend by community banks' },
  { prefix: '', value: 9400, suffix: '+', label: 'Community FIs in SAM', sub: 'Banks & credit unions in our serviceable market' },
  { prefix: '', value: 82, suffix: '%+', label: 'Banks Not on FedNow', sub: 'Unconnected as of Q2 2026 — our window' },
  { prefix: '', value: 8, suffix: 'B', label: 'Transactions in 2026', sub: 'Growing to 13.9B by 2028 at 30%+ CAGR' },
  { prefix: '<', value: 100, suffix: 'ms', label: 'Smart Router Speed', sub: 'Auto-selects optimal rail per transaction' },
  { prefix: '', value: 6, suffix: '', label: 'Live White-Label Demos', sub: 'Running now — request access below' },
];

function Counter({ value, prefix, suffix, active, duration = 2200 }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active || value === 0) { setCount(value); return; }
    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * value));
      if (progress < 1) requestAnimationFrame(animate);
      else setCount(value);
    };
    const id = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(id);
  }, [active, value, duration]);

  return (
    <span>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
}

export default function StatsSection() {
  const sectionRef = useRef(null);
  const [active, setActive] = useState(false);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          setTimeout(() => setActive(true), 300);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="stats"
      data-testid="stats-section"
      ref={sectionRef}
      className="relative section-padding"
      style={{
        scrollMarginTop: '80px',
        background: 'linear-gradient(180deg, #000 0%, #030803 50%, #000 100%)',
      }}
    >
      {/* Background glow */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[800px] h-[400px] opacity-10"
          style={{ background: 'radial-gradient(ellipse, #00FF00, transparent 70%)' }} />
      </div>

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16"
          style={{
            opacity: revealed ? 1 : 0,
            transform: revealed ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.7s ease',
          }}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="green-line" />
            <span className="text-[#00FF00] font-mono text-xs uppercase tracking-widest">By the Numbers</span>
            <div className="green-line" />
          </div>
          <h2 className="text-4xl sm:text-5xl font-black uppercase text-white leading-tight"
            style={{ fontFamily: 'Unbounded, sans-serif' }}>
            The Opportunity Is
            <span className="text-[#00FF00]"> Enormous</span>
          </h2>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[#1A1A1A]">
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              data-testid={`stat-item-${i}`}
              className="group bg-black p-8 md:p-10 relative overflow-hidden hover:bg-[#0A0A0A] transition-colors duration-300"
              style={{
                opacity: revealed ? 1 : 0,
                transform: revealed ? 'translateY(0)' : 'translateY(30px)',
                transition: `all 0.6s ease ${0.1 + i * 0.1}s`,
              }}
            >
              {/* Hover glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(0,255,0,0.08), transparent 70%)' }} />

              <div className="relative z-10">
                <div
                  className="text-4xl md:text-5xl lg:text-6xl font-black font-mono mb-2 text-white"
                  style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    textShadow: active ? '0 0 30px rgba(0,255,0,0.3)' : 'none',
                    transition: 'text-shadow 0.5s ease',
                  }}
                >
                  <span className="text-[#00FF00]">
                    <Counter
                      value={stat.value}
                      prefix={stat.prefix}
                      suffix={stat.suffix}
                      active={active}
                    />
                  </span>
                </div>
                <div className="text-white text-sm font-bold uppercase tracking-wide mb-1"
                  style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '11px' }}>
                  {stat.label}
                </div>
                <div className="text-[#A3A3A3] text-xs" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {stat.sub}
                </div>
              </div>

              {/* Bottom border highlight */}
              <div className="absolute bottom-0 left-0 w-0 h-[2px] bg-[#00FF00] group-hover:w-full transition-all duration-500" />
            </div>
          ))}
        </div>

        {/* Market sizing note */}
        <div
          className="mt-8 text-center"
          style={{
            opacity: revealed ? 1 : 0,
            transition: 'opacity 0.7s ease 0.8s',
          }}
        >
          <p className="text-[#A3A3A3] text-xs font-mono uppercase tracking-widest">
            TAM $48B &bull; SAM $12B (FedNow + RTP Ready FIs by 2027) &bull; Target $240M ARR by 2030
          </p>
        </div>
      </div>
    </section>
  );
}
